import {
    createContext,
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {ApiResponseDto, Claim, NameWithId, VivoRootModel} from "../Models";
import {GUID, VivoPortal} from "../AppConstants";
import axios from "axios";
import {
    ApiUrl_Login_ExchangeOneLoginToken,
    ApiUrl_Login_VerifyMember,
    ApiUrl_Orders_GetMyUnreadComments,
    ApiUrl_Orders_SetCommentReadPointer, ApiUrl_Orders_SetCommentReadPointerBackInTime
} from "../Endpoints";
import {JwtUtils} from "./JwtUtils";
import EnumMemberGroupAliases = VivoPortal.EnumMemberGroupAliases;
import getToken = JwtUtils.getToken;
import getClaims = JwtUtils.getClaims;
import logout = JwtUtils.logout;
import isImpersonatedInfo = JwtUtils.isImpersonatedInfo;
import {CommentNotificationDTO} from "../signalr/signalr.models";
import {
    AuthenticationResponse,
    SetCommentReadPointerBackInTimeRequest,
    SetCommentReadPointerRequest
} from "./auth.models";
import {
    useFlintNotificationContext
} from "@components/page_components/common/notifications/FlintNotificationContext";
import {boolean} from "yup";
import languageList = VivoPortal.languageList;
import {getOneLoginUser} from "@utils/onelogin/OneLoginHelper";
import useAuthenticationHelper from "@utils/auth/AuthenticationHelper";


export const AuthenticationContext = createContext<AuthenticationContextProps>(
    {
        roleMask: 0,
        claims: [],
        rootModel: undefined,
        authenticationError: "",
        lang: {
            get: languageList[0], getId: languageList[0].Id, set: () => {
            }
        },
        update: () => {
        },
        VerifyTokensAsync: (force: boolean): Promise<VivoRootModel | null> => Promise.resolve({} as VivoRootModel),
        isCurrentUserInRole: () => false,
        isInitialized: false,
        commentOrderGuids: [],
        comments: {
            get: [],
            append: () => false,
            hideOlderComments: () => undefined,
            unhideHiddenComments: () => undefined
        }
    })

export function useAuthenticationContext() {
    const context = useContext(AuthenticationContext);
    if (!context) {
        throw new Error('useAuthenticationContext must be used within an AuthenticationContextProvider');
    }
    return context;
}

export const AuthenticationContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const [roleMask, setRoleMask] = useState<EnumMemberGroupAliases>(0);
    const [claims, setClaims] = useState<Claim[]>([]);
    const [rootModel, setRootModel] = useState<VivoRootModel>();
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [comments, setComments] = useState<CommentNotificationDTO[]>([]);
    const [lang, setLang] = useState<NameWithId>(languageList[0]);
    const authenticationError = useRef<string>("");
    const {showNotification} = useFlintNotificationContext();
    const {validation} = useAuthenticationHelper();

    function isCurrentUserInRole(role: EnumMemberGroupAliases) {
        return roleMask && (roleMask & role) > 0;
    }

    useEffect(() => {
        let sum = calculateRoleMask(claims);
        setRoleMask(sum);
    }, [claims]);

    useEffect(() => {
        if (roleMask == 0)
            return;
        setIsInitialized(true);
    }, [roleMask]);

    useEffect(() => {
        VerifyTokensAsync().then();
    }, []);

    /** Method checks if user has valid credentials of any type. If he does, returns the user's rootModel.
     * If the credentials do not exist or are invalid, returns null */
    async function VerifyTokensAsync(force: boolean = false): Promise<VivoRootModel | null> {
        if (rootModel && !force)
            return rootModel;
        const token = getToken();
        if (!token) {
            let model = await validation.validateOneLoginUserAsync();
            if (typeof model !== 'string') {
                authenticationError.current = "";
                setRootModel(model);
                setClaims(getClaims());
                return model;
            }
            setIsInitialized(true);
            authenticationError.current = model;
            return null;
        }

        try {
            // 1) Try OneLogin exchange
            let model: VivoRootModel | string = await validation.validateUmbracoTokenAsync();

            // 2) If exchange failed, fall back to your own Verify
            if (typeof model === 'string') {
                model = await validation.validateOneLoginUserAsync();
            }

            // 3) Now you have a model (or you didn't, in which case model is null)
            if (typeof model !== 'string') {
                authenticationError.current = "";
                setRootModel(model);
                const claims = getClaims()
                setClaims(claims);
                let roleClaims: string[] = claims?.filter(x => x.name === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role')[0]?.value as string[] ?? [];
                if (claims && roleClaims.length == 0)
                    setIsInitialized(true);
                return model;
            }
            // token was invalid
            setIsInitialized(true);
            //authenticationError.current = model;
            await logout();
            return null;
        } catch (err) {
            console.error(err);
            await logout();
            setIsInitialized(true);
        } finally {

        }
        return null;
    }

    function calculateRoleMask(claims: Claim[]): EnumMemberGroupAliases {
        if (!claims)
            return 0;
        let roleClaims: string[] = claims?.filter(x => x.name === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role')[0]?.value as string[] ?? [];
        if (!Array.isArray(roleClaims))
            roleClaims = [roleClaims];
        return roleClaims
            .map(x => EnumMemberGroupAliases[x as keyof typeof EnumMemberGroupAliases])
            .reduce((sum, c) => sum | c, 0);
    }

    function updateAuthContext(claims: Claim[], rootModel?: VivoRootModel) {
        setClaims(claims);
        setRootModel(rootModel);
        setRoleMask(roleMask);
    }

    const impersonationInfo = isImpersonatedInfo();

    function appendComment(comment: CommentNotificationDTO) {
        const exists = comments.filter((c: CommentNotificationDTO) => {
            return comment.Id == c.Id
        }).length;
        if (exists > 0)
            return false;
        setComments(prev => [...prev, comment]);
        return true;
    }

    function hideOlderComments(lastCommentToHide: CommentNotificationDTO): Promise<void> | undefined {
        const post: SetCommentReadPointerRequest = {
            LabId: rootModel!.CurrentUserProfile.LabId,
            DivisionId: rootModel!.CurrentUserProfile.DivisionId,
            LastReadCommentId: lastCommentToHide.Id
        }
        const index = comments.indexOf(lastCommentToHide);
        if (index < 0)
            return;
        return axios.post(ApiUrl_Orders_SetCommentReadPointer, post)
            .then((response) => {
                const splicedComments = comments.splice(index + 1);
                setComments(splicedComments);
            })
            .catch((e) => showNotification(e));
    }

    function unhideHiddenComments(): Promise<number | void | undefined> | undefined {
        const post: SetCommentReadPointerBackInTimeRequest = {
            LabId: rootModel!.CurrentUserProfile.LabId,
            DivisionId: rootModel!.CurrentUserProfile.DivisionId,
            RequestedNumberOfCommentsBack: 25
        }
        return axios.post<ApiResponseDto<CommentNotificationDTO[]>>(ApiUrl_Orders_SetCommentReadPointerBackInTime, post)
            .then((response) => {
                let recoveredComments = response.data.Result;
                if (recoveredComments.length == 0)
                    return;
                const current = [...comments];
                let counter = 0;
                recoveredComments.forEach((pc) => {
                    let index = current.findIndex((c) => c.Id == pc.Id);
                    if (index < 0) {
                        current.unshift(pc);
                        counter++;
                    }
                })
                setComments(current);
                return Promise.resolve(counter);
            })
            .catch((e) => showNotification(e));
    }

    useEffect(() => {
        if (!rootModel || !rootModel.CurrentUserProfile.ShowNewCommentsPopup) {
            setComments([]);
            return;
        }
        axios.get<ApiResponseDto<CommentNotificationDTO[]>>(ApiUrl_Orders_GetMyUnreadComments)
            .then((response) => {
                const comments = response.data.Result;
                setComments(comments);
            })
            .catch((error) => { /* console.log(error) */ });
    }, [rootModel?.CurrentUserProfile.LabId, rootModel?.CurrentUserProfile.DivisionId])

    const [commentOrderGuids, setCommentOrderGuids] = useState<GUID[]>([]);
    // Get distinct OrderGuids
    useEffect(() => {
        const g = comments.reduce<GUID[]>((acc, comment) => {
            if (!acc.includes(comment.OrderGuid)) {
                acc.push(comment.OrderGuid);
            }
            return acc;
        }, []);
        setCommentOrderGuids(g);
    }, [comments.length])

    return (<AuthenticationContext.Provider
        value={{
            roleMask,
            claims,
            rootModel,
            authenticationError: authenticationError.current,
            lang: {get: lang, getId: lang.Id, set: setLang},
            update: updateAuthContext,
            VerifyTokensAsync: VerifyTokensAsync,
            isCurrentUserInRole: isCurrentUserInRole,
            isInitialized,
            commentOrderGuids,
            comments: {
                get: comments,
                append: appendComment,
                hideOlderComments,
                unhideHiddenComments,
            }
        }}>
        {impersonationInfo !== null && <div
            className={"impersonated-user-info"}>{`Impersonated by ${impersonationInfo.login} until ${impersonationInfo.expiration.toLocaleTimeString()}`}</div>}
        {children}
    </AuthenticationContext.Provider>)
};

export interface AuthenticationContextProps {

    claims: Claim[],
    rootModel?: VivoRootModel,
    /** should be filled whenever user can not be authenticated */
    authenticationError: string;
    roleMask: EnumMemberGroupAliases,
    update: (setClaims: Claim[], setRootModel?: VivoRootModel) => void,
    isCurrentUserInRole: (role: EnumMemberGroupAliases) => boolean;
    isInitialized: boolean;
    commentOrderGuids: GUID[];
    VerifyTokensAsync: (force: boolean) => Promise<VivoRootModel | null>;
    lang: { get: NameWithId, getId: number, set: Dispatch<SetStateAction<NameWithId>> }
    comments: {
        get: CommentNotificationDTO[],
        append: (newComment: CommentNotificationDTO) => boolean
        hideOlderComments: (lastCommentToHide: CommentNotificationDTO) => Promise<void> | undefined,
        unhideHiddenComments: () => Promise<number | void | undefined> | undefined
    }
}